// TRUST-GENERAL-ACCOUNTS-1 (2026-06-17) — Operations → General Trust.
// Foundation surface ("don't overbuild; don't lose"): per-account trust cash
// balance (direction-aware net of trust_transactions) + the Ops-loan ledger
// (Operating advancing cash into a trust, repaid later). Outstanding per
// account = Σ advances − Σ repayments. Server-gated manage_reimbursement_intake.
import { useState, useEffect, useCallback } from "react";
import { apiFetch, fmt } from "../api";
import { Spinner } from "./ui";  // UI-SWEEP-ACCOUNTING

const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" };

export default function GeneralTrust() {
  const [data, setData] = useState(null);
  const [loans, setLoans] = useState([]);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(false);

  const load = useCallback(() => {
    apiFetch("/trust/general").then(setData).catch(e => setErr(String(e.message || e)));
    apiFetch("/trust/ops-loans").then(d => setLoans(d.ops_loans || [])).catch(() => setLoans([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="alert-error">{err}</div>;
  if (!data) return <Spinner label="Loading trust activity…" />;

  return (
    <div>
      <div className="section-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="section-title">General Trust</span>
        <button className="btn btn-blue btn-sm" onClick={() => setModal(true)}>+ Record Ops loan</button>
      </div>

      {/* Per-account balances */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Trust accounts</h3>
          <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 400, marginTop: 2 }}>
            Balance = net of trust transactions (in − out). Ops loan outstanding = advances − repayments.
          </div>
        </div>
        <table>
          <thead><tr>
            <th>Account</th><th>Kind</th>
            <th className="text-right">Trust Balance</th>
            <th className="text-right">Ops Loan Outstanding</th>
          </tr></thead>
          <tbody>
            {data.accounts.map(a => (
              <tr key={a.id}>
                <td style={{ fontWeight: 600 }}>{a.account_name}</td>
                <td style={{ fontSize: 12, color: "var(--text2)" }}>{a.trust_account_kind || "—"}</td>
                <td className="text-right" style={mono}>{fmt.money(a.ledger_balance)}</td>
                <td className="text-right" style={{ ...mono, fontWeight: a.ops_loan_outstanding ? 700 : 400, color: a.ops_loan_outstanding ? "#b45309" : "var(--text3)" }}>
                  {fmt.money(a.ops_loan_outstanding)}
                </td>
              </tr>
            ))}
            <tr style={{ borderTop: "2px solid var(--border)", fontWeight: 700 }}>
              <td colSpan={2} style={{ textAlign: "right", fontStyle: "italic", color: "var(--text2)" }}>Total</td>
              <td className="text-right" style={mono}>{fmt.money(data.totals.ledger_balance)}</td>
              <td className="text-right" style={{ ...mono, color: data.totals.ops_loan_outstanding ? "#b45309" : "var(--text3)" }}>{fmt.money(data.totals.ops_loan_outstanding)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Funding-source reference */}
      <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 16 }}>
        Trust funds: {data.funding_sources.map(f => f.name).join(" · ")}
      </div>

      {/* Ops-loan ledger */}
      <div className="card">
        <div className="card-header"><h3>Ops loan ledger</h3></div>
        {loans.length === 0
          ? <div className="empty-state">No Ops loans recorded.</div>
          : <table>
              <thead><tr>
                <th>Date</th><th>Account</th><th>Direction</th>
                <th className="text-right">Amount</th><th>Counterparty</th><th>Memo</th><th></th>
              </tr></thead>
              <tbody>
                {loans.map(l => (
                  <tr key={l.id}>
                    <td style={{ ...mono, fontSize: 11, color: "var(--text3)" }}>{fmt.date(l.loan_date)}</td>
                    <td style={{ fontSize: 12 }}>{l.account_name}</td>
                    <td>
                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3,
                        color: l.direction === "advance" ? "#b45309" : "#166534",
                        background: l.direction === "advance" ? "#fef3c7" : "#dcfce7", padding: "1px 6px", borderRadius: 3 }}>
                        {l.direction}
                      </span>
                    </td>
                    <td className="text-right" style={mono}>{fmt.money(l.amount)}</td>
                    <td style={{ fontSize: 12, color: "var(--text2)" }}>{l.counterparty || "—"}</td>
                    <td style={{ fontSize: 12, color: "var(--text2)", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.memo}>{l.memo || "—"}</td>
                    <td style={{ textAlign: "right" }}>
                      <button title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 12, color: "#dc2626" }}
                        onClick={() => { if (window.confirm("Delete this Ops loan entry?")) apiFetch(`/trust/ops-loans/${l.id}`, { method: "DELETE" }).then(load).catch(e => alert(e.message || "Delete failed.")); }}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>

      {modal && <OpsLoanModal accounts={data.accounts} onClose={() => setModal(false)} onDone={() => { setModal(false); load(); }} />}
    </div>
  );
}

function OpsLoanModal({ accounts, onClose, onDone }) {
  const [f, setF] = useState({ trust_account_id: accounts[0]?.id || "", direction: "advance", amount: "", loan_date: "", counterparty: "WNW Operating", memo: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const inp = { width: "100%", padding: "7px 9px", fontSize: 13, border: "1px solid var(--border)", borderRadius: 5, boxSizing: "border-box" };
  const save = () => {
    if (!f.trust_account_id) { setErr("Select an account."); return; }
    if (f.amount === "" || isNaN(Number(f.amount)) || Number(f.amount) <= 0) { setErr("Amount must be greater than 0."); return; }
    if (!f.loan_date) { setErr("Date is required."); return; }
    setBusy(true); setErr(null);
    apiFetch("/trust/ops-loans", { method: "POST", body: { ...f, trust_account_id: Number(f.trust_account_id) } })
      .then(onDone).catch(e => { setErr(e.message || "Save failed."); setBusy(false); });
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, padding: "6vh 16px" }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{ background: "white", width: 480, maxWidth: "100%", padding: 20, borderRadius: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: "var(--navy)" }}>Record Ops loan</h3>
          <button className="btn btn-sm btn-outline" onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 12 }}>Advance = Operating → Trust. Repayment = Trust → Operating.</div>
        {err && <div style={{ background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FCA5A5", padding: "8px 10px", borderRadius: 5, fontSize: 13, marginBottom: 12 }}>{err}</div>}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)" }}>Trust account
            <select style={inp} value={f.trust_account_id} onChange={e => setF({ ...f, trust_account_id: e.target.value })}>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)" }}>Direction
            <select style={inp} value={f.direction} onChange={e => setF({ ...f, direction: e.target.value })}>
              <option value="advance">Advance (Operating → Trust)</option>
              <option value="repayment">Repayment (Trust → Operating)</option>
            </select>
          </label>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)" }}>Amount
            <input type="number" step="0.01" style={inp} value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} />
          </label>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)" }}>Date
            <input type="date" style={inp} value={f.loan_date} onChange={e => setF({ ...f, loan_date: e.target.value })} />
          </label>
        </div>
        <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", display: "block", marginTop: 12 }}>Counterparty
          <input style={inp} value={f.counterparty} onChange={e => setF({ ...f, counterparty: e.target.value })} />
        </label>
        <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", display: "block", marginTop: 12 }}>Memo
          <textarea style={{ ...inp, minHeight: 48 }} value={f.memo} onChange={e => setF({ ...f, memo: e.target.value })} />
        </label>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button className="btn btn-sm btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-sm btn-blue" onClick={save} disabled={busy}>Record</button>
        </div>
      </div>
    </div>
  );
}
